import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BadgeCheck,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  FileBadge2,
  Gauge,
  ShieldAlert,
  Truck,
  Wrench,
  X,
  XCircle,
} from "lucide-react";
import { createVehicleInspection, listVehicleReadiness } from "../api";
import type { AuthUser, Vehicle, VehicleReadiness } from "../types";

type ReadinessFilter = "all" | "blocked" | "warning" | "ready";
type Result = "pass" | "fail" | "na";

const statusLabel = {
  blocked: "BLOQUEADA",
  warning: "CON SEGUIMIENTO",
  ready: "LIBERABLE",
};

const checklist = [
  ["lights", "Luces y señalización", "electrical_lighting"],
  ["tires", "Llantas y presión", "tire_system"],
  ["brakes", "Frenos de servicio y estacionamiento", "brake_system"],
  ["fluids", "Niveles y fugas", "engine_fluids"],
  ["emergency_kit", "Equipo de emergencia", "safety_equipment"],
] as const;

export function VehicleReadinessPanel({
  open,
  onClose,
  vehicles,
  user,
  onSelectVehicle,
}: {
  open: boolean;
  onClose: () => void;
  vehicles: Vehicle[];
  user: AuthUser;
  onSelectVehicle: (vehicle: Vehicle) => void;
}) {
  const [rows, setRows] = useState<VehicleReadiness[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filter, setFilter] = useState<ReadinessFilter>("all");
  const [inspectionOpen, setInspectionOpen] = useState(false);
  const [results, setResults] = useState<Record<string, Result>>(
    Object.fromEntries(checklist.map(([code]) => [code, "pass"])),
  );
  const [notes, setNotes] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const refresh = () => {
      listVehicleReadiness()
        .then((items) => {
          setRows(items);
          setSelectedId((current) =>
            items.some((item) => item.vehicle_id === current)
              ? current
              : items[0]?.vehicle_id ?? null,
          );
          setError("");
        })
        .catch((cause) =>
          setError(cause instanceof Error ? cause.message : "No fue posible cargar unidades"),
        );
    };
    refresh();
    window.addEventListener("gsf:readiness-updated", refresh);
    return () => window.removeEventListener("gsf:readiness-updated", refresh);
  }, [open]);

  const visibleRows = useMemo(
    () => rows.filter((row) => filter === "all" || row.readiness_status === filter),
    [filter, rows],
  );
  const selected =
    visibleRows.find((row) => row.vehicle_id === selectedId) || visibleRows[0] || rows[0];
  const canInspect =
    user.permissions?.includes("fleet.readiness.manage") ??
    ["superadmin", "fleet_admin", "operator", "dispatcher", "maintenance"].includes(user.role);
  const counts = useMemo(
    () => ({
      blocked: rows.filter((row) => row.readiness_status === "blocked").length,
      warning: rows.filter((row) => row.readiness_status === "warning").length,
      ready: rows.filter((row) => row.readiness_status === "ready").length,
    }),
    [rows],
  );

  async function submitInspection() {
    if (!selected) return;
    setPending(true);
    setError("");
    try {
      const updated = await createVehicleInspection(selected.vehicle_id, {
        odometer_km: selected.odometer_km ?? undefined,
        notes,
        items: checklist.map(([item_code, label, component_code]) => ({
          item_code,
          label,
          component_code,
          result: results[item_code],
        })),
      });
      setRows((current) =>
        current.map((row) => row.vehicle_id === updated.vehicle_id ? updated : row),
      );
      setInspectionOpen(false);
      setNotes("");
      setResults(Object.fromEntries(checklist.map(([code]) => [code, "pass"])));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No fue posible guardar la inspección");
    } finally {
      setPending(false);
    }
  }

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside className="admin-panel readiness-panel" role="dialog" aria-modal="true" aria-labelledby="readiness-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span className="eyebrow">FLOTILLA · EXPEDIENTE Y CONTROL DE LIBERACIÓN</span>
            <h2 id="readiness-title">Estado operativo de unidades</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar unidades"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><ShieldAlert size={17} /><b>{counts.blocked}</b> bloqueadas</span>
          <span><CalendarClock size={17} /><b>{counts.warning}</b> seguimiento</span>
          <span><BadgeCheck size={17} /><b>{counts.ready}</b> liberables</span>
        </section>
        <div className="readiness-panel__body">
          <section className="readiness-queue">
            <div className="readiness-filters">
              {(["all", "blocked", "warning", "ready"] as ReadinessFilter[]).map((item) => (
                <button type="button" className={filter === item ? "is-active" : ""} onClick={() => setFilter(item)} key={item}>
                  {{ all: "TODAS", blocked: "BLOQUEADAS", warning: "SEGUIMIENTO", ready: "LIBERABLES" }[item]}
                </button>
              ))}
            </div>
            <div className="readiness-list">
              {visibleRows.map((row) => (
                <button
                  type="button"
                  className={`readiness-item readiness-item--${row.readiness_status}${selected?.vehicle_id === row.vehicle_id ? " is-selected" : ""}`}
                  onClick={() => setSelectedId(row.vehicle_id)}
                  key={row.vehicle_id}
                >
                  <span>{row.readiness_status === "ready" ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}</span>
                  <span>
                    <small>{row.unit_code} · {row.plate}</small>
                    <strong>{row.vehicle_name}</strong>
                    <em>{row.make} {row.model} · {row.reasons[0] || "Expediente completo"}</em>
                  </span>
                  <b>{statusLabel[row.readiness_status]}</b>
                </button>
              ))}
              {!visibleRows.length && <p className="maintenance-empty">SIN UNIDADES EN ESTA VISTA</p>}
            </div>
          </section>
          <section className="readiness-detail">
            {selected && (
              <>
                <div className={`readiness-status readiness-status--${selected.readiness_status}`}>
                  {selected.readiness_status === "ready" ? <BadgeCheck size={34} /> : <ShieldAlert size={34} />}
                  <span><small>CONTROL INTERNO</small><b>{statusLabel[selected.readiness_status]}</b></span>
                </div>
                <div className="readiness-detail__title">
                  <span className="eyebrow">{selected.unit_code} · {selected.plate}</span>
                  <h3>{selected.vehicle_name}</h3>
                  <p>{selected.make} {selected.model} {selected.year} · {(selected.odometer_km || 0).toLocaleString("es-MX")} km</p>
                </div>
                <div className="readiness-documents">
                  {selected.documents.map((document) => (
                    <article className={`document-card document-card--${document.status}`} key={document.id}>
                      <FileBadge2 size={18} />
                      <span>
                        <small>{document.label}</small>
                        <strong>{document.identifier}</strong>
                        <em>Vence {new Date(`${document.expires_on}T12:00:00`).toLocaleDateString("es-MX")} · {document.days_remaining} días</em>
                      </span>
                    </article>
                  ))}
                </div>
                <div className="readiness-checklist">
                  <div className="admin-section-title"><span><ClipboardCheck size={14} /> ÚLTIMA INSPECCIÓN PREOPERATIVA</span><b>{selected.latest_inspection ? new Date(selected.latest_inspection.inspected_at).toLocaleString("es-MX") : "SIN REGISTRO"}</b></div>
                  {selected.latest_inspection?.items.map((item) => (
                    <article className={`inspection-result inspection-result--${item.result}`} key={item.id}>
                      {item.result === "pass" ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                      <span><strong>{item.label}</strong><small>{item.notes || item.component_code || "Verificado"}</small></span>
                      <b>{item.result === "pass" ? "APROBADO" : item.result === "fail" ? "FALLA" : "N/A"}</b>
                    </article>
                  ))}
                  {!selected.latest_inspection && <p className="maintenance-empty">INSPECCIÓN REQUERIDA</p>}
                </div>
                <div className={`readiness-action readiness-action--${selected.readiness_status}`}>
                  <Wrench size={19} />
                  <span><small>ACCIÓN REQUERIDA</small><strong>{selected.required_action}</strong></span>
                </div>
                <div className="readiness-reasons">
                  {(selected.reasons.length ? selected.reasons : ["Sin bloqueos operativos activos."]).map((reason) => (
                    <article key={reason}><Gauge size={13} /><strong>{reason}</strong></article>
                  ))}
                </div>
                <div className="readiness-actions">
                  <button type="button" onClick={() => {
                    const vehicle = vehicles.find((item) => item.id === selected.vehicle_id);
                    if (vehicle) onSelectVehicle(vehicle);
                  }}><Truck size={13} /> VER EN CENTRO C5</button>
                  {canInspect && <button type="button" onClick={() => setInspectionOpen((value) => !value)}><ClipboardCheck size={13} /> NUEVA INSPECCIÓN</button>}
                </div>
                {inspectionOpen && (
                  <div className="inspection-form">
                    <span className="eyebrow">CHECKLIST DE LIBERACIÓN · VIGENCIA 24 H</span>
                    {checklist.map(([code, label]) => (
                      <article key={code}>
                        <strong>{label}</strong>
                        <div>
                          {(["pass", "fail", "na"] as Result[]).map((result) => (
                            <button type="button" className={results[code] === result ? `is-${result}` : ""} onClick={() => setResults((current) => ({ ...current, [code]: result }))} key={result}>
                              {{ pass: "OK", fail: "FALLA", na: "N/A" }[result]}
                            </button>
                          ))}
                        </div>
                      </article>
                    ))}
                    <textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Observaciones del inspector..." />
                    <button type="button" disabled={pending} onClick={submitInspection}>{pending ? "REGISTRANDO..." : "FIRMAR Y REGISTRAR INSPECCIÓN"}</button>
                  </div>
                )}
                {error && <p className="admin-error">{error}</p>}
              </>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}
